import React, { ReactElement } from 'react';

import AnonEmployerAssociation from 'buyside/components/presentation/EmployerAssociation/anonEmployerAssociation';
import AnonymousUserDisplay from 'buyside/components/presentation/AnonymousUserDisplay/anonymousUserDisplay';
import EmployerAssociation from 'buyside/components/presentation/EmployerAssociation/employerAssociation';
import HomePagePanel, { DataType } from 'buyside/components/presentation/HomePage/homePagePanel';
import Link from 'buyside/components/presentation/Link/link';
import MessageBubbleIcon from 'buyside/svgs/message_bubble.svg';
import ProfileImage, {
  IMAGE_SIZES
} from 'buyside/components/presentation/ProfileImage/profileImage';
import ProfileImageLoading from 'buyside/components/presentation/ProfileImage/profileImageLoading';
import Skeleton, { SKELETON_WIDTHS } from 'buyside/components/presentation/Skeleton/skeleton';

import { times } from 'buyside/util/lodashAlt';
import { truncateText } from 'buyside/util/textHelpers';
import {
  defaultUserImageUrl,
  ideaCommentsUrl,
  ideaUpdateUrl,
  userUrl
} from 'buyside/util/urlHelper';

import { useHomePageCommentsLazyQuery } from 'interfaces/graphql-typings';

import styles from 'buyside/components/presentation/HomePage/homePageStyles.scss';

const HomePageComments = (): ReactElement => (
  <HomePagePanel
    icon={MessageBubbleIcon}
    type={DataType.COMMENTS}
    useLazyQuery={useHomePageCommentsLazyQuery}
  >
    {({ loading, data }) => (
      <>
        {loading && !data && (
          <>
            {times(20).map(i => (
              <div key={i} className={styles.item}>
                <div className={styles.innerItem}>
                  <div className={styles.heading}>
                    <div>
                      <Skeleton width={SKELETON_WIDTHS.LARGE} />
                    </div>
                    <div className={styles.postedAt}>
                      <Skeleton width={SKELETON_WIDTHS.TINY} />
                    </div>
                    <div className={styles.commenterDisplay}>
                      <ProfileImageLoading size={IMAGE_SIZES.TINY} />
                      <div>
                        <Skeleton width={SKELETON_WIDTHS.MEDIUM} />
                      </div>
                    </div>
                    <Skeleton containerHeight={40} />
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
        {data?.homeComments.length === 0 && <p>No recent results matching the criteria.</p>}
        {data?.homeComments.map(
          ({ id, commentObject, anonUser, parsedBody, byAllocator, commentCreationTime, user }) => {
            let sourceIdea, linkTo;
            if (commentObject?.__typename === 'Idea') {
              sourceIdea = commentObject;
              linkTo = ideaCommentsUrl(sourceIdea.id);
            } else if (commentObject?.__typename === 'IdeaUpdate') {
              sourceIdea = commentObject.idea;
              linkTo = sourceIdea ? ideaUpdateUrl(sourceIdea.id, commentObject.id) : null;
            }
            if (!sourceIdea) return null;

            let commenterDisplay;
            if (anonUser) {
              commenterDisplay = (
                <>
                  <AnonymousUserDisplay anonUser={anonUser} />
                  <AnonEmployerAssociation
                    aumRange={anonUser.aumRange}
                    fundType={anonUser.fundType}
                    employerStyles={styles.userEmployment}
                  />
                </>
              );
            } else if (user) {
              commenterDisplay = (
                <>
                  {byAllocator ? (
                    <strong>{user.displayName}</strong>
                  ) : (
                    <Link to={userUrl(user.id)}>{user.displayName}</Link>
                  )}
                  <EmployerAssociation
                    mostRecentEmployer={user.mostRecentEmployer}
                    style={styles.userEmployment}
                  />
                </>
              );
            }

            return (
              <div className={styles.item} key={id}>
                <div className={styles.innerItem}>
                  <div className={styles.heading}>
                    <div>
                      <Link className={styles.itemTitle} to={linkTo} target="_blank">
                        {sourceIdea.entityName}
                      </Link>
                      <span className={styles.bbgSymbol}>{sourceIdea.assetSymbol}</span>
                    </div>
                    <div className={styles.postedAt}>{commentCreationTime} ago</div>
                    <div className={styles.commenterDisplay}>
                      <ProfileImage
                        source={user?.profileImageUrl || defaultUserImageUrl()}
                        size="tiny"
                        style={styles.commentImage}
                      />
                      <div>{commenterDisplay}</div>
                    </div>
                    <Link className={styles.linkText} to={linkTo} title={sourceIdea.entityName}>
                      {truncateText(parsedBody, 155)}
                    </Link>
                  </div>
                </div>
              </div>
            );
          }
        )}
      </>
    )}
  </HomePagePanel>
);

export default HomePageComments;
